// src/rms/ads/useSectionRotation.js
// ============================================================================
// Rotate ads on every move between sections
// ============================================================================
// Moving from Dashboard to Billing (or any other section) serves a fresh set of
// advertisers, uncapped. The in-page frequency cap still applies while a staff
// member stays put on one screen, so the rail does not churn while they type.
//
// Two modes:
//
//   soft reload (VITE_RMS_AD_RELOAD_ON_NAV=false)
//     Client-side rotation. Instant, no white screen. Serves exactly the same
//     number of impressions.
//
//   hard reload (default, =true)
//     A real document navigation on section change. Slower — the bundle is
//     re-parsed each time — but gives ad networks a genuinely fresh page
//     context, which is how most of them count an impression.
// ============================================================================

import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { rotateNow } from "./adEngine";
import { isTransactionalRoute } from "./adInventory";

const HARD_RELOAD = (import.meta.env.VITE_RMS_AD_RELOAD_ON_NAV ?? "true") !== "false";

/** The top-level section of a path: /rms/inventory/123 â†’ "inventory". */
export function sectionOf(pathname) {
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");
  // parts[0] === "rms"
  return parts[1] || "dashboard";
}

export default function useSectionRotation({ adsEnabled }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastSection = useRef(null);

  useEffect(() => {
    const section = sectionOf(location.pathname);
    const previous = lastSection.current;
    lastSection.current = section;

    // First render of a section (including after a hard reload) — nothing to do.
    if (previous === null || previous === section) return;

    if (!adsEnabled) {
      rotateNow();
      return;
    }

    // Never bounce a staff member out of a live transaction. A reload mid
    // checkout would lose unsaved condition readings and signatures.
    if (isTransactionalRoute(location.pathname)) {
      rotateNow();
      return;
    }

    if (HARD_RELOAD && navigationType !== "POP") {
      // Advance the rotation BEFORE unloading. The counter is persisted, so the
      // freshly loaded page picks up where this one left off instead of
      // restarting at zero and re-serving the same advertisers.
      rotateNow();
      window.location.assign(location.pathname + location.search);
      return;
    }

    rotateNow();
  }, [location.pathname, location.search, navigationType, adsEnabled]);
}

export { HARD_RELOAD };
